import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { ArrowRight } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      title: 'Free Practice Audit',
      description: 'We analyze your current billing performance, identify revenue leaks, and provide a detailed improvement roadmap—no obligation.',
    },
    {
      number: '02',
      title: 'Onboarding & System Integration',
      description: 'Seamless setup and integration with your existing EHR/PM system. We handle all technical details and staff training.',
    },
    {
      number: '03',
      title: 'We Bill, Code & Follow Up',
      description: 'Our certified team handles all coding, claims submission, denials, and patient billing while you focus on care.',
    },
    {
      number: '04',
      title: 'You Get Paid Faster, With Full Visibility',
      description: 'Watch your revenue increase through our real-time dashboard. Your dedicated manager ensures optimal performance.',
    },
  ];

  return (
    <section id="how-it-works" className="py-32 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="text-[#14b8a6] font-semibold text-sm tracking-wider uppercase mb-4">
            Simple & Seamless
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            How It Works
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Get started in 4 simple steps and start seeing revenue improvements
            within the first billing cycle.
          </p>
        </motion.div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#1e3a8a]/20 via-[#14b8a6]/30 to-[#1e3a8a]/20 transform -translate-x-1/2" />

          <div className="space-y-24">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <div className="flex-1">
                  <Card className="border-2 border-slate-100 hover:border-[#14b8a6]/30 transition-all duration-300 hover:shadow-xl group">
                    <CardContent className="p-8 lg:p-10">
                      <div className="flex items-start gap-6">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="text-6xl font-bold bg-gradient-to-br from-[#1e3a8a] to-[#14b8a6] bg-clip-text text-transparent"
                        >
                          {step.number}
                        </motion.div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-[#1e3a8a] transition-colors">
                            {step.title}
                          </h3>
                          <p className="text-slate-600 leading-relaxed text-lg">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Center number badge */}
                <motion.div
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="hidden lg:flex w-20 h-20 rounded-full bg-gradient-to-br from-[#1e3a8a] to-[#14b8a6] items-center justify-center shadow-lg z-10"
                >
                  <ArrowRight className="text-white" size={28} />
                </motion.div>

                {/* Spacer for alternating layout */}
                <div className="flex-1 hidden lg:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
